import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
const Joi = require("@hapi/joi");
const countries = require("../countries");

import TextInput from "./textInput";
import ValidationSummary from "./validationSummary";

const userSchema = Joi.object({
  name: Joi.string().required(),
  email: Joi.string()
    .required()
    .min(3)
    .max(200)
    .regex(/(.+)@(.+){2,}\.(.+){2,}/)
    .message("Must be a valid email address"),
  optIn: Joi.boolean(),
});

const StripeForm = ({
  brand,
  customPrice,
  plan,
  setCurrency,
  setCountryCode,
  onPaymentMethodSuccessful,
  defaultCurrency,
  countryCode,
  validateGame,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitted, setSubmitted] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  const [user, setUser] = useState({
    name: "",
    email: "",
    optIn: false,
  });

  const [validationErrors, setValidationErrors] = useState([]);

  async function handleChangeCountry(e) {
    let value = e.target.value;
    const country = countries.find((c) => c.code === value);
    let currency = "GBP";
    if (country) {
      currency = country.currency || "USD";
    }
    setCurrency(currency);
    setCountryCode(value);
  }

  function getPayButtonText(c) {
    const customPriceText = customPrice
      ? `${customPrice} ${defaultCurrency}`
      : null;
    return `Pay ${customPrice ? customPriceText : plan.cost[c].priceText}`;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    if (!submitted) {
      setSubmitted(true);
      const gameValidationResult = validateGame();
      if (gameValidationResult) {
        let validationResult = userSchema.validate(user);
        if (validationResult.error) {
          setValidationErrors(validationResult.error.details);
          setSubmitted(false);
        } else {
          setValidationErrors([]);
          setPaymentProcessing(true);
          const paymenIntentUrl = brand
            ? `${brand.rootUrl}/api/payment-intent`
            : "/api/payment-intent";
          // get payment intent
          const res = await fetch(paymenIntentUrl, {
            body: JSON.stringify({
              plan,
              currency: defaultCurrency,
              country: countryCode,
              email: user.email,
              optIn: user.optIn,
              customPrice,
            }),
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });
          const { clientSecret } = await res.json();

          if (!clientSecret) {
            setGlobalError("Something went wrong. No payment was taken");
            setSubmitted(false);
            setPaymentProcessing(false);
            return;
          }

          const result = await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: elements.getElement(CardElement),
              billing_details: {
                name: user.name,
                email: user.email,
                address: { country: countryCode },
              },
            },
          });
          if (result.error) {
            setGlobalError(result.error.message);
            setPaymentProcessing(false);
            setSubmitted(false);
          } else {
            onPaymentMethodSuccessful({
              paymentIntent: result.paymentIntent,
              user,
            });
          }
        }
      } else {
        setSubmitted(false);
      }
    }
  };

  const handleChange = (e) => {
    let value = e.target.value;
    if (e.target.name === "email") {
      value = value.trim();
    }

    setUser(Object.assign({}, user, { [e.target.name]: value }));
  };

  return (
    <div className={paymentProcessing ? "processing payments" : "payments"}>
      {globalError ? (
        <div className="notification is-danger global-error is-light">
          {globalError}
        </div>
      ) : null}
      <form onSubmit={handleSubmit}>
        <fieldset className="FormGroup">
          <ValidationSummary validationErrors={validationErrors} />
          <div className="field">
            <label className="label">Country</label>
            <div className="control">
              <div className="select is-fullwidth">
                <select value={countryCode} onChange={handleChangeCountry}>
                  {countries.map((c) => {
                    return (
                      <option key={`plan-${c.code}`} value={c.code}>
                        {c.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>
          <div className="field">
            <label htmlFor="name" className="label">
              Name on card
            </label>
            <div className="control">
              <TextInput
                validationErrors={validationErrors}
                value={user.name}
                onChange={handleChange}
                name="name"
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="email" className="label">
              Email
            </label>
            <div className="control">
              <TextInput
                validationErrors={validationErrors}
                value={user.email}
                onChange={handleChange}
                name="email"
              />
            </div>
          </div>
          <div className="field">
            <label className="label">Card Details</label>
            <div className="StripeElement control">
              <CardElement
                options={{
                  base: {
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "20px",
                    fontFamily: '"Noto Sans JP", sans-serif;',
                    fontSmoothing: "antialiased",
                    ":focus": {
                      color: "#424770",
                    },

                    "::placeholder": {
                      color: "#9BACC8",
                    },

                    ":focus::placeholder": {
                      color: "#CFD7DF",
                    },
                  },
                  invalid: {
                    color: "#fff",
                    ":focus": {
                      color: "#FA755A",
                    },
                    "::placeholder": {
                      color: "#FFCCA5",
                    },
                  },
                }}
              />
            </div>
          </div>
        </fieldset>
        <button
          className="button is-fullwidth  is-large"
          type="submit"
          disabled={!stripe || paymentProcessing}
        >
          {paymentProcessing ? (
            "Processing"
          ) : (
            <span>{getPayButtonText(defaultCurrency)}</span>
          )}
        </button>
        <style jsx>{`
          .processing {
          }
          .FormGroup {
            padding: 0.5em 0.75em;
            background-color: white;
            will-change: opacity, transform;
            box-shadow: 0 6px 9px rgba(50, 50, 93, 0.06),
              0 2px 5px rgba(0, 0, 0, 0.08), inset 0 1px 0 #829fff;
            border-radius: 4px;
            margin-bottom: 1em;
          }

          .FormRow {
            display: -ms-flexbox;
            display: flex;
            -ms-flex-align: center;
            align-items: center;
            border-top: 1px solid #819efc;
          }

          .StripeElement--webkit-autofill {
            background: transparent !important;
          }

          .StripeElement {
            box-shadow: inset 0 0.0625em 0.125em rgba(10, 10, 10, 0.05);
            width: 100%;
            padding: 0.5em 0.85em;
            border: 1px solid #dbdbdb;
            border-radius: 4px;
            color: #363636;
          }
          .pad-bottom {
            margin-bottom: 0.5em;
          }
          .price-slider {
            width: 100%;
          }
          .global-error {
            position: fixed;
            width: 100%;
            top: 0px;
            z-index: 100;
          }
          .consent input {
            margin-right: 10px;
          }
          .consent {
            margin: 2em 0;
            text-align: center;
            font-size: 0.8em;
          }
        `}</style>
      </form>
    </div>
  );
};

export default StripeForm;
